<template>
	<div class="card-page survey-wrapper">
		<ja-header
			ref="header"
			:title="$t('gamePlayMetaTitles.questionnaire')"
			subtitle="JA Finance Park"
		>
			<template v-slot:icon>
				<svgicon name="headerIcons/questionnaire"></svgicon>
			</template>
		</ja-header>
		<content>
			<div class="iframe-wrapper">
				<div ref="surveyContainer" class="survey-embed"></div>
			</div>
		</content>
		<footer>
			<div class="footer-item">
				<ja-button
					class="small right"
					name="next"
					:disabled="!onComplete"
					:aria-label="$t('goToQuestionnaire')"
					@click.native.prevent="proceed"
					><span class="button-text">{{ $t('next') }}</span>
					<svgicon name="arrow-right" class="arrow-right"></svgicon
				></ja-button>
			</div>
		</footer>
		<div class="button-wrapper"></div>
	</div>
</template>

<script>
import gql from 'graphql-tag';
import Button from '../components/ui/Button';
import Header from '../components/ui/headers/Header';
import UserData from '../mixins/UserData';
import NavigationLoading from '../mixins/NavigationLoading';
import { ValidateProfileStepBeforeProceed, ProfileData } from '@/mixins';

const SURVEY_SDK_ID = 'smcx-sdk';
const SURVEY_SDK_URL =
	'https://widget.surveymonkey.com/collect/website/js/tRaiETqnLgj758hTBazgd5TegXp_2FhA4RLN4mpzG_2FOZAr_2FMlQaWaEmoOoaFEDREY0.js';

export default {
	components: {
		jaHeader: Header,
		jaButton: Button
	},
	mixins: [
		UserData,
		NavigationLoading,
		ValidateProfileStepBeforeProceed,
		ProfileData
	],
	data() {
		return {
			questions: [],
			onComplete: false,
			selectedAnswer: null,
			skipQuery: false
		};
	},
	computed: {
		userData() {
			return this.$store.getters.userData;
		},
		constants() {
			return this.$store.getters.constants;
		},
		answers() {
			const answers = {};
			if (this.questions.length) {
				for (var i = 0, len = this.questions.length; i < len; i++) {
					let currentQuestionID = this.questions[i].lifestyle_Question_ID;

					answers[currentQuestionID] = {
						type: this.questions[i].cmsLifestyleQuestionFormat.questionFormat,
						value: null
					};
				}
			}

			return answers;
		}
	},
	watch: {
		$route() {
			this.selectedAnswer = null;
			this.skipQuery = false;
			this.$apollo.queries.questions.refetch();
		}
	},
	mounted() {
		this.$store.commit('setLoading', false);
		window.addEventListener('message', this.handleSurveyMessage);
		this.loadSurveySdk();
	},
	created() {
		if (this.userData) {
			this.checkForProfileData();
			this.$store.dispatch('getPauseState');
		}
	},
	apollo: {
		questions: {
			query: gql`
				query questions($questionsFilter: String!) {
					cmsLifestyleQuestions(
						where: $questionsFilter
						order: "questionOrder"
						take: 1
					) {
						lifestyle_Question_ID
						questionOrder
						question
						lifeStage_ID
						cmsLifestyleQuestionType {
							questionType
						}
						cmsLifestyleQuestionFormat {
							questionFormat
						}
						cmsLifestyleQuestionAnswerOptions {
							lifestyle_Question_Option_ID
							text
							optionOrder
						}
					}
				}
			`,
			fetchPolicy: 'no-cache',
			skip() {
				return this.skipQuery;
			},
			variables() {
				return {
					questionsFilter: this.getQuestionsFilters()
				};
			},
			update: function(data) {
				console.log(data);
				const filteredQuestions = data.cmsLifestyleQuestions.filter(
					(question, index, self) =>
						index === self.findIndex(q => q.question === question.question)
				);
				this.$store.commit('setLoading', false);

				return filteredQuestions;
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		this.skipQuery = true;
		next();
	},
	beforeDestroy() {
		window.removeEventListener('message', this.handleSurveyMessage);
		this.unloadSurveySdk();
	},
	methods: {
		loadSurveySdk() {
			this.unloadSurveySdk();
			window.SMCX = window.SMCX || [];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.async = true;
			script.id = SURVEY_SDK_ID;
			script.src = SURVEY_SDK_URL;
			this.$refs.surveyContainer.appendChild(script);
		},
		unloadSurveySdk() {
			const script = document.getElementById(SURVEY_SDK_ID);
			if (script && script.parentNode) {
				script.parentNode.removeChild(script);
			}
			if (this.$refs.surveyContainer) {
				this.$refs.surveyContainer.innerHTML = '';
			}
			document
				.querySelectorAll('[class*="smcx-"]')
				.forEach(node => node.parentNode && node.parentNode.removeChild(node));
			delete window.SMCX;
		},
		async sendAnswers() {
			this.$store.commit('setLoading', true);

			this.requestMade = true;

			let list = [];
			for (let key in this.answers) {
				let obj = this.answers[key];
				let answerObj = {};
				answerObj.isPost = true;
				answerObj.studentId = this.userData.studentId;
				answerObj.simulation_Lifestyle_Questions_ID = key;
				switch (obj.type) {
					case 'True / False':
						answerObj.boolOptionAnswer = true;
						list.push(answerObj);
						break;
				}
			}

			// set default falue. the value may or may not be set later on
			let response = null;

			// Use variable as it is more desciptive
			const isMutationListEmpty = !list.length;

			// if list is not empty, proceed as usual
			if (!isMutationListEmpty) {
				response = await this.$apollo.mutate({
					mutation: gql`
						mutation($list: [StudentQuestionAnswerAddInputObjectGraphType]) {
							addManyStudentQuestionAnswer(list: $list) {
								id
								cmsLifestyleQuestionAnswerOption {
									text
								}
							}
						}
					`,
					variables: {
						list: list
					}
				});
			}

			// if we have response data or list is empty
			// redirect to where you have to redirect
			if ((response && response.data) || isMutationListEmpty) {
				this.$router.push({ name: 'studentSummary' });
			}
			this.requestMade = false;
		},
		getQuestionsFilters() {
			const filters = [];

			const questionType = 'Pre/Post';

			// valid state for all types of questions
			filters.push('isActive=true && IsUpperLevelQuestion=true');

			filters.push(`cmsLifestyleQuestionType.questionType="${questionType}"`);

			filters.push(`IsAtEndOfSimulation = true`);

			return filters.join('&&');
		},
		async proceed() {
			this.sendAnswers();
		},
		handleSurveyMessage(event) {
			let hostname;
			try {
				hostname = new URL(event.origin).hostname;
			} catch (e) {
				return;
			}
			if (!/(^|\.)surveymonkey\.com$/.test(hostname)) {
				return;
			}
			let data = event.data;
			if (typeof data === 'string') {
				try {
					data = JSON.parse(data);
				} catch (e) {
					return;
				}
			}
			if (data && data.eventName === 'sm:thank_you_page_loaded') {
				this.onComplete = true;
			}
		}
	}
};
</script>
<style lang="scss" scoped>
.survey-wrapper {
	width: 100%;
	header {
		width: 100%;
		button {
			float: right;
		}
	}

	content {
		width: 100%;
		overflow-y: auto;
		flex-grow: 1;
		padding-top: 50px;

		.iframe-wrapper {
			width: 100%;
			max-width: 850px;
			margin: auto;

			.survey-embed ::v-deep iframe {
				width: 100%;
				max-width: 850px;
				border: none;

				@media screen and (orientation: portrait) {
					height: 75vh;
					max-height: 75vh;
				}

				@media screen and (orientation: landscape) and (min-height: 450px) {
					height: 55vh;
					max-height: 55vh;
				}
			}
		}
	}
	footer {
		display: flex;
		justify-content: flex-end;
		align-items: center;

		&.progress-tracker {
			flex-grow: 1;
		}
	}
}
</style>
